import React from "react";
import {
    Calendar,
    Callout,
    Checkbox,
    DayOfWeek,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    DetailsRow,
    DirectionalHint,
    Fabric,
    FocusTrapZone,
    IBasePickerSuggestionsProps,
    IColumn,
    IDetailsListProps,
    IDetailsRowStyles,
    IDropdownOption,
    ITag,
    Link,
    PrimaryButton,
    SelectionMode,
    Stack,
    TagPicker,
    Text,
} from "office-ui-fabric-react";
import { Loading } from "../../services/Loading";
import { ConfirmDialogContext } from "../common/ConfirmDialogProvider";
import {
    childrenGap,
    childrenGap8,
    DayPickerStrings
} from "../common/Constants";
import NoDataTile from "../common/NoDataTile";
import { ToastContext } from "../common/ToastProvider";
import ManagePmDialog from "../customers/previous-meetings/ManagePmDialog";
import { IAdmin, IPreviousMeeting } from "../../vm";
import { deletePreviousMeeting, getPreviousMeetings, getPreviousMeetingsExcel } from "../../services/customers/CustomerService";
import { mdiDotsVertical } from "@mdi/js";
import moment from "moment";
import Icon from "@mdi/react";
import PageTitle from "../common/PageTitle";
import PMIcon from "../../images/meetings-icon.svg";
import { parseJwt } from "../../services/UtilService";
import { getAllAdmins, getCemAssignToACustomer } from "../../services/datacenter/DataCenterManagerService";
import ManagePmSendEmailDialog from "../customers/previous-meetings/ManagePmSendEmailDialog";
import TotalNumbers from "../common/TotalNumbers";

export interface CustomerMeetingMainProps { }
let calendarButtonElement: HTMLElement;

const CustomerMeetingMain: React.FC<CustomerMeetingMainProps> = () => {
    // custom hooks
    const { showToast } = React.useContext(ToastContext);
    const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
    const tokenData = parseJwt();

    const queryParams = new URLSearchParams(window.location.search);

    // state
    const [state, setState] = React.useState({
        isLoading: false,
        previousMeetings: [],
        customers: [],
        searchObj: {
            startDate: queryParams.get("startDate")
                ? queryParams.get("startDate")
                : moment().startOf("month").toDate(),
            endDate: queryParams.get("endDate")
                ? queryParams.get("endDate")
                : moment().endOf("month").toDate(),
            status: [],
            ignoreDateRange: queryParams.get("ignoreDateRange") ? queryParams.get("ignoreDateRange") === 'true' ? true : false : true,
            cemIds: queryParams.get("cemId") ? [queryParams.get("cemId")] : tokenData.role === 2 ? [tokenData._id] : [],
            customerIds: [],
            sortBy: "meetingDate",
            sortOrder: "asc",
            // sortOrder: "desc",
            sendCustomerName: true
        },
        manageAcDialog: {
            isOpen: false,
            index: undefined,
            data: undefined,
            customerName: undefined,
            isStepTwoView: false
        },
        manageActionsCallout: {
            isOpen: false,
            index: undefined,
            element: undefined,
            data: undefined,
        },
        showCallender: false,
        userListByCustomerId: {},
        userDict: {},
    } as {
        isLoading: boolean;
        previousMeetings: IPreviousMeeting[];
        customers: ITag[];
        searchObj: {
            startDate: Date | string;
            endDate: Date | string;
            ignoreDateRange: boolean;
            cemIds: string[];
            customerIds: string[];
            sortBy: "meetingDate" | "noOfActionItems" | "noOfOpenItems" | "noOfWipItems" | "noOfClosedItems";
            sortOrder: "asc" | "desc";
            sendCustomerName: boolean
        };
        manageAcDialog: {
            isOpen: boolean;
            index?: number;
            data?: IPreviousMeeting;
            customerName?: string;
            isStepTwoView: boolean
        };
        manageActionsCallout: {
            isOpen: boolean;
            element: any;
            index?: number;
            data?: IPreviousMeeting;
        };
        showCallender: boolean;
        userListByCustomerId: { [key: string]: IDropdownOption[] };
        userDict: { [key: string]: IAdmin };
    });
    const [cemUserList, setCemUserList] = React.useState([] as ITag[]);
    const [selectedMeetingIds, setSelectedMeetingIds] = React.useState([] as string[]);
    const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = React.useState(false as boolean);

    React.useEffect(() => {
        const asyncFunc = async () => {
            setState({ ...state, isLoading: true });
            await getAllCemUsers();
            await getAllPreviousMeeting();
        };
        asyncFunc();
    }, []);

    const getAllPreviousMeeting = async (paramState?: any) => {
        let localState = { ...state };
        if (paramState) {
            localState = { ...paramState };
            setState({ ...localState, isLoading: true });
        }

        let result = await getPreviousMeetings(localState.searchObj);

        if (result?.success) {
            let customers = [...state.customers];
            let customerIds: string[] = [];
            result.data.forEach((d) => {
                customerIds.push(d.customerId);
                const found = customers.find((dd) => dd.key === d.customerId);
                if (!found) customers.push({ key: d.customerId, name: d.customerName });
            });
            let obj = {
                ...localState,
                previousMeetings: result.data,
                customers
            };
            setSelectedMeetingIds([]);
            if (customerIds.length > 0) {
                await getCemUsers(obj, customerIds);
            } else {
                setState({
                    ...obj, isLoading: false,
                });
            }
        } else {
            setState({ ...state, isLoading: false });
            showToast(
                result?.message || "Error while getting customer meetings",
                "error"
            );
        }
    };

    const getCemUsers = async (paramState: any, customerIds: string[]) => {
        let result = await getCemAssignToACustomer(customerIds);
        if (result?.success) {
            const dict: any = {};
            const listArrayDict = Object.keys(result.data).reduce((acc: any, customerId: string) => {
                let data = result.data[customerId] || [];
                acc[customerId] = data.map((d) => {
                    dict[d._id as string] = d;
                    return { key: d._id, text: d.name };
                })
                return acc;
            }, {});
            setState({
                ...state,
                ...paramState,
                userDict: dict,
                userListByCustomerId: listArrayDict,
                isLoading: false,
            });
        } else {
            setState({ ...paramState, isLoading: false });
            showToast(
                result?.message || "Error while getting cem users",
                "error"
            );
        }
    };

    const getAllCemUsers = async () => {
        let result = await getAllAdmins({ role: 2, sendEnabledItems: true });
        if (result && result.success) {
            let list: ITag[] = result.data.map((d: IAdmin) => ({ key: d._id, name: d.name }));
            setCemUserList(list);
        } else {
            showToast(result?.message || "Error while getting Cem users", "error");
        }
    };

    const handleDialogOpen = (isOpen: boolean, index?: number, isStepTwoView?: boolean) => {
        let previousMeetings: any = undefined;
        let customerName = undefined;
        if (index !== undefined) {
            previousMeetings = { ...state.previousMeetings[index] };
            customerName = state.customers.find((d) => d.key === previousMeetings?.customerId)?.name;
        }
        setState({
            ...state,
            manageAcDialog: {
                isOpen,
                index,
                data: previousMeetings,
                customerName,
                isStepTwoView: isStepTwoView as boolean
            },
        });
    };

    const handleDialogClose = async (data: IPreviousMeeting) => {
        // let previousMeetings = [...state.previousMeetings];
        if (data) {
            // if (state.manageAcDialog.index !== undefined) {
            //     // let previousMeetings = [...state.previousMeetings];
            //     // previousMeetings[state.manageAcDialog.index] = data;
            //     // setState({
            //     //   ...state,
            //     //   manageAcDialog: {
            //     //     isOpen: false,
            //     //     index: undefined,
            //     //     data: undefined,
            //     //   },
            //     //   previousMeetings,
            //     // });
            //     showToast("Customer Meeting updated successfully", "success");
            // } else {
            //     // previousMeetings.unshift(data);
            //     showToast("Customer Meeting added successfully", "success");
            // }
            await getAllPreviousMeeting({
                ...state,
                manageAcDialog: {
                    isOpen: false,
                    index: undefined,
                    data: undefined,
                    isStepTwoView: false
                }
            });
        } else {
            setState({
                ...state,
                manageAcDialog: {
                    isOpen: false,
                    index: undefined,
                    data: undefined,
                    isStepTwoView: false
                },
            });
        }
        // setState({
        //   ...state,
        //   manageAcDialog: {
        //     isOpen: false,
        //     index: undefined,
        //     data: undefined,
        //   },
        //   previousMeetings,
        // });
    };

    const handleEmailDialogOpenClose = (isOpen: boolean, isSendEmail?: boolean) => {
        setIsSendEmailDialogOpen(isOpen);
        if (isSendEmail) setSelectedMeetingIds([]);
    }

    const handleLoading = () => {
        setState({
            ...state,
            isLoading: !state.isLoading,
        });
    };

    const deletePm = async (item: IPreviousMeeting, index: number) => {
        showConfirmDialog(
            "Are You Sure",
            "Do you want to delete customer meeting",
            async () => {
                setState({
                    ...state,
                    isLoading: true,
                    manageActionsCallout: {
                        isOpen: false,
                        index: undefined,
                        element: undefined,
                        data: undefined,
                    },
                });
                let result = await deletePreviousMeeting(item);
                if (result?.success) {
                    showToast("Customer Meeting deleted successfully", "success");
                    let previousMeetings = [...state.previousMeetings];
                    previousMeetings.splice(index, 1);
                    setState({
                        ...state,
                        previousMeetings,
                        isLoading: false,
                        manageActionsCallout: {
                            isOpen: false,
                            index: undefined,
                            element: undefined,
                            data: undefined,
                        },
                    });
                } else {
                    setState({
                        ...state,
                        isLoading: false,
                        manageActionsCallout: {
                            isOpen: false,
                            index: undefined,
                            element: undefined,
                            data: undefined,
                        },
                    });
                    showToast(
                        result.message || "Error while deleting customer meeting",
                        "error"
                    );
                }
            }
        );
    };

    const _renderItemColumn = (item: any, index: number, column: IColumn) => {
        switch (column.key) {
            case "actions":
                return (
                    (tokenData.isAdmin && tokenData.role === 0) || item.userId === tokenData._id ? (
                        <span
                            onClick={(ele) => handleCallOutOpenClose(true, ele, index, item)}
                        >
                            <Icon
                                path={mdiDotsVertical}
                                size={0.9}
                                className="pointer"
                                color="#135c8a"
                            />
                        </span>
                    ) : (
                        <></>
                    )
                );
            case "sttParticipant":
                return <span>{`${item.sttParticipantName} (${item.sttParticipantEmail})`}</span>;
            case "customerParticipant":
                return <span>{`${item.customerParticipantName} (${item.customerParticipantEmail})`}</span>;
            case "meetingDate":
                return <span>{item.meetingDate ? moment(item.meetingDate).format("DD/MM/YYYY HH:mm A") : "NA"}</span>;
            case "customerId":
                return <Link style={{ fontWeight: 600, textDecoration: "underline" }} onClick={() => handleDialogOpen(true, index, true)}>{item.customerName}</Link>;
            // return (
            //     <Text style={{ fontWeight: 600 }}>
            //         {item.customerName}
            //     </Text>
            // );
            case "userId":
                return <span>{state.userDict[item.userId]?.name || "NA"}</span>;
            case "noOfActionItems":
                return <Link style={{ fontWeight: 600, textDecoration: "underline" }} onClick={() => handleDialogOpen(true, index, true)}>{item.noOfActionItems}</Link>;
            case "checkbox":
                return (
                    (tokenData.isAdmin && tokenData.role === 0) || item.userId === tokenData._id ?
                        <Checkbox
                            checked={selectedMeetingIds.includes(item._id)}
                            onChange={(e, v) => {
                                let meetingIds = [...selectedMeetingIds];
                                const index = meetingIds.findIndex((d) => d === item._id);
                                if (index !== -1) {
                                    meetingIds.splice(index, 1);
                                } else {
                                    meetingIds.push(item._id);
                                }
                                setSelectedMeetingIds(meetingIds);
                            }}
                        /> : <></>
                );
            default:
                return <span>{column.fieldName ? item[column.fieldName] : ""}</span>;
        }
    };

    const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
        const customStyles: Partial<IDetailsRowStyles> = {};
        if (props) {
            if (props.itemIndex % 2 !== 0) {
                customStyles.root = { backgroundColor: "#F3F3F3" };
            }

            return <DetailsRow {...props} styles={customStyles} />;
        }
        return null;
    };

    const onColumnClick = async (ev: React.MouseEvent<HTMLElement, MouseEvent>, column: IColumn) => {
        let searchObj = { ...state.searchObj };
        if (searchObj.sortBy === column.key) {
            searchObj.sortOrder = searchObj.sortOrder === "desc" ? "asc" : "desc";
        } else {
            searchObj.sortBy = column.key as any;
            searchObj.sortOrder = "asc";
        }
        await getAllPreviousMeeting({ ...state, searchObj });
    };

    const handleCallOutOpenClose = (
        isOpen: boolean,
        ele?: React.MouseEvent<HTMLSpanElement>,
        index?: number,
        data?: IPreviousMeeting
    ) => {
        setState({
            ...state,
            manageActionsCallout: {
                isOpen,
                element: isOpen ? ele?.currentTarget : undefined,
                index,
                data,
            },
        });
    };

    const closeCallender = (ev?: any) => {
        setState({
            ...state,
            showCallender: false,
        });
    };

    const cemPickerSuggestionsProps: IBasePickerSuggestionsProps = {
        noResultsFoundText: "No cem found",
    };

    const customerPickerSuggestionsProps: IBasePickerSuggestionsProps = {
        noResultsFoundText: "No customer found",
    };

    const filterSuggestedTagsForCem = (filterText: string, tagList: ITag[]): ITag[] => {
        let allCemTags = [...cemUserList];

        return filterText
            ? allCemTags.filter(
                (tag) =>
                    tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
                    !listContainsTagList(tag, tagList)
            )
            : [];
    };

    const filterSuggestedTagsForCustomer = (filterText: string, tagList: ITag[]): ITag[] => {
        let allCustomerTags = [...state.customers];

        return filterText
            ? allCustomerTags.filter(
                (tag) =>
                    tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
                    !listContainsTagList(tag, tagList)
            )
            : [];
    };

    const showAllTagsForCem = (selectedItems?: ITag[] | undefined) => {
        let allCemTags = [...cemUserList];

        return removeDuplicates(allCemTags, selectedItems);
    };

    const showAllTagsForCustomer = (selectedItems?: ITag[] | undefined) => {
        let allCustomerTags = state.customers;

        return removeDuplicates(allCustomerTags, selectedItems);
    };

    function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
        return tagList.filter((tag) => !listContainsTagList(tag, tags));
    }

    const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
        if (!tagList || !tagList.length || tagList.length === 0) {
            return false;
        }
        return tagList.some((compareTag) => compareTag.key === tag.key);
    };

    const downloadReport = async () => {
        const result = await getPreviousMeetingsExcel(state.searchObj);
        let xlsx = await result.blob();
        const xlFile = await new Blob([xlsx], {
            type: "application/vnd.openxmlformats",
        });
        const xlFileURL = await URL.createObjectURL(xlFile);
        var a: any = document.createElement("a");
        document.body.appendChild(a);
        a.style = "display:none";
        a.href = xlFileURL;
        a.download = `MOR Customer Meetings List.xlsx`;
        a.click();
    };

    return (
        <React.Fragment>
            {state.isLoading && <Loading />}
            <Stack tokens={childrenGap}>
                <Stack.Item>
                    <PageTitle
                        icon={PMIcon}
                        title="Customer Meetings"
                        description="List of all customer meetings"
                    />
                </Stack.Item>
                <Stack.Item>
                    <Stack tokens={childrenGap} horizontal horizontalAlign="space-between">
                        <Stack.Item>
                            <Stack horizontal wrap tokens={childrenGap} verticalAlign="center">
                                <Stack.Item>
                                    <div
                                        ref={(calendarBtn) => (calendarButtonElement = calendarBtn!)}
                                    >
                                        <DefaultButton
                                            text={
                                                !state.searchObj.startDate
                                                    ? "Select a month"
                                                    : moment(state.searchObj.startDate).format("MMM-YYYY")
                                            }
                                            onClick={() => setState({ ...state, showCallender: true })}
                                            className="month-picker-btn"
                                            disabled={state.searchObj.ignoreDateRange}
                                        />
                                    </div>
                                    {state.showCallender && (
                                        <Callout
                                            isBeakVisible={false}
                                            // className="ms-DatePicker-callout"
                                            gapSpace={0}
                                            doNotLayer={false}
                                            target={calendarButtonElement}
                                            directionalHint={DirectionalHint.bottomLeftEdge}
                                            onDismiss={() => closeCallender()}
                                            setInitialFocus
                                        >
                                            <FocusTrapZone
                                                firstFocusableSelector="ms-DatePicker-day--today"
                                                isClickableOutsideFocusTrap
                                            >
                                                <Calendar
                                                    // eslint-disable-next-line react/jsx-no-bind
                                                    onSelectDate={async (date: Date | null | undefined) => {
                                                        // setState()
                                                        let obj = {
                                                            ...state,
                                                            searchObj: {
                                                                ...state.searchObj,
                                                                startDate: moment(date)
                                                                    .startOf("month")
                                                                    .format("YYYY-MM-DD"),
                                                                endDate: moment(date)
                                                                    .endOf("month")
                                                                    .format("YYYY-MM-DD"),
                                                            },
                                                            showCallender: false,
                                                        };
                                                        await getAllPreviousMeeting(obj);
                                                    }}
                                                    onDismiss={() => closeCallender()}
                                                    isMonthPickerVisible={true}
                                                    value={new Date(state.searchObj.startDate)}
                                                    firstDayOfWeek={DayOfWeek.Sunday}
                                                    strings={DayPickerStrings}
                                                    isDayPickerVisible={false}
                                                    highlightCurrentMonth={false}
                                                    highlightSelectedMonth={true}
                                                    showMonthPickerAsOverlay={false}
                                                    showGoToToday={false}
                                                    maxDate={new Date(Date.now())}
                                                />
                                            </FocusTrapZone>
                                        </Callout>
                                    )}
                                </Stack.Item>
                                <Stack.Item>
                                    <TagPicker
                                        selectedItems={state.customers.filter((d) => state.searchObj.customerIds.includes(d.key as string))}
                                        removeButtonAriaLabel="Remove"
                                        onResolveSuggestions={filterSuggestedTagsForCustomer as any}
                                        pickerSuggestionsProps={customerPickerSuggestionsProps}
                                        onEmptyResolveSuggestions={showAllTagsForCustomer}
                                        inputProps={{
                                            placeholder: "Select a customer",
                                        }}
                                        styles={{ root: { minWidth: "200px" } }}
                                        onChange={async (items: any) => {
                                            let customerIds: string[] = [];
                                            items.forEach((i: any) => {
                                                customerIds.push(i.key);
                                            });
                                            let obj = {
                                                ...state,
                                                searchObj: {
                                                    ...state.searchObj,
                                                    customerIds
                                                },
                                            };
                                            await getAllPreviousMeeting(obj as any);
                                        }}
                                    />
                                </Stack.Item>
                                <Stack.Item>
                                    <TagPicker
                                        selectedItems={cemUserList.filter((d) => state.searchObj.cemIds.includes(d.key as string))}
                                        removeButtonAriaLabel="Remove"
                                        onResolveSuggestions={filterSuggestedTagsForCem as any}
                                        pickerSuggestionsProps={cemPickerSuggestionsProps}
                                        onEmptyResolveSuggestions={showAllTagsForCem}
                                        inputProps={{
                                            placeholder: "Select a cem",
                                        }}
                                        styles={{ root: { minWidth: "200px" } }}
                                        onChange={async (items: any) => {
                                            let cemIds: string[] = [];
                                            items.forEach((i: any) => {
                                                cemIds.push(i.key);
                                            });
                                            let obj = {
                                                ...state,
                                                searchObj: {
                                                    ...state.searchObj,
                                                    cemIds
                                                },
                                            };
                                            await getAllPreviousMeeting(obj as any);
                                        }}
                                    />
                                </Stack.Item>
                                <Stack.Item>
                                    <Checkbox
                                        label="Show All data irrespective of date"
                                        checked={state.searchObj.ignoreDateRange}
                                        onChange={async (e, v) => {
                                            let obj = {
                                                ...state,
                                                searchObj: {
                                                    ...state.searchObj,
                                                    ignoreDateRange: v
                                                },
                                            };
                                            await getAllPreviousMeeting(obj as any);
                                        }}
                                    />
                                </Stack.Item>
                            </Stack>
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
                <Stack.Item>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                        <Stack.Item>
                            <TotalNumbers value={state.previousMeetings.length} />
                        </Stack.Item>
                        <Stack.Item>
                            <Stack tokens={childrenGap} horizontal wrap horizontalAlign="end">
                                {tokenData.isAdmin && [0, 2].includes(tokenData.role) && selectedMeetingIds.length > 0 && (
                                    <Stack.Item>
                                        <PrimaryButton text="Send Email" onClick={() => handleEmailDialogOpenClose(true)} />
                                    </Stack.Item>
                                )}
                                <Stack.Item>
                                    <PrimaryButton text="Export" onClick={downloadReport} disabled={state.previousMeetings.length === 0} />
                                </Stack.Item>
                                {tokenData.isAdmin && [0, 2].includes(tokenData.role) && (
                                    <Stack.Item>
                                        <PrimaryButton
                                            text="Add Customer Meeting"
                                            onClick={() => handleDialogOpen(true)}
                                            className="borderless-btn bg-white"
                                        />
                                    </Stack.Item>
                                )}
                            </Stack>
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
                <Stack.Item>
                    {state.previousMeetings && state.previousMeetings.length > 0 ? (
                        <Fabric>
                            <DetailsList
                                items={state.previousMeetings}
                                columns={tokenData.isAdmin && [0, 2].includes(tokenData.role) ? [
                                    {
                                        key: "checkbox",
                                        name: "Select",
                                        fieldName: "checkbox",
                                        minWidth: 50,
                                        maxWidth: 50,
                                        isResizable: false,
                                    },
                                    {
                                        key: "actions",
                                        name: "Actions",
                                        fieldName: "actions",
                                        minWidth: 60,
                                        maxWidth: 60,
                                        isResizable: true,
                                    },
                                    {
                                        key: "customerId",
                                        name: "Customer",
                                        fieldName: "customerId",
                                        minWidth: 250,
                                        maxWidth: 300,
                                        isResizable: true,
                                    },
                                    {
                                        key: "meetingDate",
                                        name: "Meeting Date",
                                        fieldName: "meetingDate",
                                        minWidth: 150,
                                        maxWidth: 200,
                                        isResizable: true,
                                        isSorted: state.searchObj.sortBy === "meetingDate",
                                        isSortedDescending: state.searchObj.sortOrder === "desc",
                                        onColumnClick: onColumnClick
                                    },
                                    {
                                        key: "sttParticipant",
                                        name: "STT Participant (Name, Email)",
                                        fieldName: "sttParticipant",
                                        minWidth: 200,
                                        maxWidth: 250,
                                        isResizable: true,
                                    },
                                    {
                                        key: "customerParticipant",
                                        name: "Customer Participant (Name, Email)",
                                        fieldName: "customerParticipant",
                                        minWidth: 250,
                                        maxWidth: 300,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfActionItems",
                                        name: "No Of Action Items",
                                        fieldName: "noOfActionItems",
                                        minWidth: 140,
                                        maxWidth: 180,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfOpenItems",
                                        name: "Open",
                                        fieldName: "noOfOpenItems",
                                        minWidth: 70,
                                        maxWidth: 70,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfWipItems",
                                        name: "Work In Progress",
                                        fieldName: "noOfWipItems",
                                        minWidth: 135,
                                        maxWidth: 135,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfClosedItems",
                                        name: "Closed",
                                        fieldName: "noOfClosedItems",
                                        minWidth: 70,
                                        maxWidth: 70,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfUnderObservationItems",
                                        name: "Under Observation",
                                        fieldName: "noOfUnderObservationItems",
                                        minWidth: 140,
                                        maxWidth: 140,
                                        isResizable: true,
                                    },
                                    {
                                        key: "userId",
                                        name: "Added By",
                                        fieldName: "userId",
                                        minWidth: 170,
                                        maxWidth: 220,
                                        isResizable: true,
                                    },
                                ] : [
                                    {
                                        key: "customerId",
                                        name: "customer",
                                        fieldName: "customerId",
                                        minWidth: 150,
                                        maxWidth: 200,
                                        isResizable: true,
                                    },
                                    {
                                        key: "meetingDate",
                                        name: "Meeting Date",
                                        fieldName: "meetingDate",
                                        minWidth: 150,
                                        maxWidth: 200,
                                        isResizable: true,
                                        isSorted: state.searchObj.sortBy === "meetingDate",
                                        isSortedDescending: state.searchObj.sortOrder === "desc",
                                        onColumnClick: onColumnClick
                                    },
                                    {
                                        key: "sttParticipant",
                                        name: "STT Participant (Name, Email)",
                                        fieldName: "sttParticipant",
                                        minWidth: 200,
                                        maxWidth: 250,
                                        isResizable: true,
                                    },
                                    {
                                        key: "customerParticipant",
                                        name: "Customer Participant (Name, Email)",
                                        fieldName: "customerParticipant",
                                        minWidth: 250,
                                        maxWidth: 300,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfActionItems",
                                        name: "No Of Action Items",
                                        fieldName: "noOfActionItems",
                                        minWidth: 140,
                                        maxWidth: 180,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfOpenItems",
                                        name: "Open",
                                        fieldName: "noOfOpenItems",
                                        minWidth: 70,
                                        maxWidth: 70,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfWipItems",
                                        name: "Work In Progress",
                                        fieldName: "noOfWipItems",
                                        minWidth: 135,
                                        maxWidth: 135,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfClosedItems",
                                        name: "Closed",
                                        fieldName: "noOfClosedItems",
                                        minWidth: 70,
                                        maxWidth: 70,
                                        isResizable: true,
                                    },
                                    {
                                        key: "noOfUnderObservationItems",
                                        name: "Under Observation",
                                        fieldName: "noOfUnderObservationItems",
                                        minWidth: 140,
                                        maxWidth: 140,
                                        isResizable: true,
                                    },
                                    {
                                        key: "userId",
                                        name: "Added By",
                                        fieldName: "userId",
                                        minWidth: 170,
                                        maxWidth: 220,
                                        isResizable: true,
                                    },
                                ]}
                                compact={true}
                                setKey="set"
                                onRenderItemColumn={_renderItemColumn as any}
                                layoutMode={DetailsListLayoutMode.justified}
                                selectionMode={SelectionMode.none}
                                onRenderRow={_onRenderRow}
                            />
                        </Fabric>
                    ) : (
                        <NoDataTile title="No Customer Meetings Found"></NoDataTile>
                    )}
                </Stack.Item>
            </Stack>
            {state.manageActionsCallout.isOpen && (
                <Callout
                    styles={{ root: { width: 95, padding: 20 } }}
                    role="alertdialog"
                    ariaLabelledBy={
                        state.manageActionsCallout && state.manageActionsCallout.data?._id
                    }
                    gapSpace={0}
                    target={state.manageActionsCallout.element}
                    directionalHint={DirectionalHint.leftCenter}
                    onDismiss={() => handleCallOutOpenClose(false)}
                // setInitialFocus={true}
                >
                    <Stack tokens={childrenGap8}>
                        <Stack.Item className="pointer">
                            <span
                                onClick={() => {
                                    handleDialogOpen(true, state.manageActionsCallout.index);
                                }}
                                className="callout-options"
                            >
                                Edit
                            </span>
                        </Stack.Item>
                        <Stack.Item className="pointer">
                            <span
                                onClick={() => {
                                    deletePm(
                                        state.manageActionsCallout.data as IPreviousMeeting,
                                        state.manageActionsCallout.index as number
                                    );
                                }}
                                className="callout-options"
                            >
                                Delete
                            </span>
                        </Stack.Item>
                    </Stack>
                </Callout>
            )}
            {state.manageAcDialog.isOpen && (
                <ManagePmDialog
                    data={state.manageAcDialog.data}
                    handleLoading={handleLoading}
                    handleDialogClose={(data: IPreviousMeeting) =>
                        handleDialogClose(data)
                    }
                    customerId={""}
                    // customerId={params.customerId}
                    userList={state.userListByCustomerId[state.manageAcDialog.data?.customerId as string] || []}
                    isFromDashboard={true}
                    customerName={state.manageAcDialog.customerName}
                    isStepTwoView={state.manageAcDialog.isStepTwoView}
                />
            )}
            {isSendEmailDialogOpen && (
                <ManagePmSendEmailDialog
                    meetingIds={selectedMeetingIds}
                    handleClose={(isSendEmail?: boolean) =>
                        handleEmailDialogOpenClose(false, isSendEmail)
                    }
                    meetingList={state.previousMeetings}
                />
            )}
        </React.Fragment>
    );
};

export default CustomerMeetingMain;
